// -----------------------------------------------------------------------------
// Anaglyphic, Netherlands @ 2023.
// See README in the root project for more information.
// -----------------------------------------------------------------------------

import Echo from "laravel-echo";
import Pusher from "pusher-js";
import { createApiClient } from "@/api/base";
import { useBaseStore } from "@/stores/base";

const api = createApiClient();

declare global {
	interface Window {
		Pusher: typeof Pusher;
		Echo: Echo<"reverb">;
	}
}

/**
 * Initialises Laravel Echo against the Reverb WebSocket server.
 *
 * Private and presence channels are authorised through the backend
 * `/api/broadcasting/auth` endpoint, reusing the existing Sanctum cookie
 * session (the axios client sends credentials and the XSRF token header
 * automatically).
 */
export const initEcho = (reverbConfig): Echo<"reverb"> => {
	window.Pusher = Pusher;

	window.Echo = new Echo<"reverb">({
		broadcaster: "reverb",
		key: reverbConfig.key,
		wsHost: window.location.hostname,
		wsPort: Number(import.meta.env.VITE_REVERB_PORT ?? 80),
		wssPort: Number(import.meta.env.VITE_REVERB_PORT ?? 443),
		forceTLS: (import.meta.env.VITE_REVERB_SCHEME ?? "https") === "https",
		enabledTransports: ["ws", "wss"],
		authorizer: (channel) => ({
			authorize: (socketId, callback) => {
				api
					.post("broadcasting/auth", {
						socket_id: socketId,
						channel_name: channel.name,
					})
					.then((response) => callback(null, response.data))
					.catch((error) => callback(error, null));
			},
		}),
	});

	return window.Echo;
};

export const tenantChannel = (name: string) => `tenant.${name}`;
